import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, FileSpreadsheet, FileText, Users, 
  TrendingUp, UserCheck, UserX, AlertCircle, 
  RefreshCw, ChevronLeft, ChevronRight, Eye, X,
  GraduationCap, BookOpen, PieChart, CheckCircle, XCircle,
  Clock, Plus, Trash2, Search
} from 'lucide-react';
import api from '../api';

const COESubjectMarks = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { context, subject } = location.state || {};

  const goBackToDashboard = () => {
    navigate('/coe/dashboard', {
      state: { context },
      replace: true
    });
  };
  
  // State Declarations
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState(null);
  const [facultyDetails, setFacultyDetails] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadType, setDownloadType] = useState('all');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
  const [orgInfo, setOrgInfo] = useState({ name: '', logoUrl: '', address: '', phone: '', email: '' });
  const [exportLoading, setExportLoading] = useState(false);
  const [signatories, setSignatories] = useState([
    { id: 1, designation: 'Assistant Professor', type: 'faculty' },
    { id: 2, designation: 'Controller of Examinations', type: 'coe' },
    { id: 3, designation: 'Principal', type: 'principal' }
  ]);

  // Logging Effect
  useEffect(() => {
    if (showDetailsModal && selectedStudentDetails?.subjectData?.componentScores) {
      console.log('View Modal: componentScores', selectedStudentDetails.subjectData.componentScores);
    }
  }, [showDetailsModal, selectedStudentDetails]);

  // Fetch organization info
  useEffect(() => {
    const fetchOrgInfo = async () => {
      const tenantId = localStorage.getItem('tenantId') || context?.tenantId || 'TYN31126910';
      try {
        const response = await api.get(`/api/organization/basic-info/${tenantId}`);
        if (response.data.success) {
          setOrgInfo({
            name: response.data.name || '',
            logoUrl: response.data.logoUrl || '',
            address: response.data.address || '',
            phone: response.data.phone || '',
            email: response.data.email || ''
          });
        }
      } catch (err) {
        console.error('Error fetching org info:', err);
      }
    };
    fetchOrgInfo();
  }, [context]);

  // Helper to get profile image URL
  const getProfileImageUrl = (profilePhotoUrl) => {
    if (!profilePhotoUrl) return null;
    const backendUrl = api.defaults.baseURL || 'http://localhost:5000';
    return profilePhotoUrl.startsWith('http') 
      ? profilePhotoUrl 
      : `${backendUrl}${profilePhotoUrl}`;
  };

  // Format date in 12-hour format
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatTo2 = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num.toFixed(2) : '0.00';
  };

  // Fetch subject marks
  const fetchSubjectMarks = useCallback(async () => {
    if (!context || !subject) {
      setError('Missing context or subject information');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.append('programTypeId', context.programTypeId);
      params.append('programId', context.programId);
      params.append('academicYearId', context.academicYearId);
      params.append('subjectId', subject.subjectId);
      
      if (context.semesterId) params.append('semesterId', context.semesterId);
      if (context.termId) params.append('termId', context.termId);
      
      const response = await api.get(`/api/coe/marks/overview?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        let filteredStudents = data.students || [];
        
        filteredStudents.sort((a, b) => (a.studentUSN || '').localeCompare(b.studentUSN || ''));
        
        setStudents(filteredStudents);
        setTotalPages(Math.ceil(filteredStudents.length / 10));
        setSummary(data.summary);
        
        if (filteredStudents.length > 0 && filteredStudents[0].subjects?.length > 0) {
          const subjectData = filteredStudents[0].subjects.find(s => s.subjectId === subject.subjectId);
          if (subjectData) {
            setFacultyDetails({
              facultyName: subjectData.facultyName || subject.facultyName || 'Not Assigned',
              facultyId: subject.facultyId,
              lastCalculated: subjectData.lastCalculated
            });
            setSignatories(prev => prev.map(s => 
              s.type === 'faculty' ? { ...s, name: subjectData.facultyName || subject.facultyName || '' } : s
            ));
          }
        }
      }
    } catch (err) {
      console.error('Error fetching subject marks:', err);
      setError(err.response?.data?.message || 'Failed to fetch subject marks');
    } finally {
      setLoading(false);
    }
  }, [context, subject]);

  useEffect(() => {
    fetchSubjectMarks();
  }, [fetchSubjectMarks]);

  const filteredStudents = students.filter(student => 
    student.studentName?.toLowerCase().includes(search.toLowerCase()) ||
    student.studentUSN?.toLowerCase().includes(search.toLowerCase())
  );

  const paginatedStudents = filteredStudents.slice(
    (currentPage - 1) * 10,
    currentPage * 10
  );

  const getEligibilityBadge = (isEligible) => {
    return isEligible 
      ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle className="w-3.5 h-3.5" /> Eligible</span>
      : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200"><XCircle className="w-3.5 h-3.5" /> Not Eligible</span>;
  };

  const getAttendanceColor = (percentage) => {
    if (percentage >= 75) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
    if (percentage >= 60) return 'text-amber-600 bg-amber-50 border-amber-100';
    return 'text-rose-600 bg-rose-50 border-rose-100';
  };

  const addSignatory = () => {
    const newId = Math.max(...signatories.map(s => s.id), 0) + 1;
    setSignatories([...signatories, { id: newId, designation: '', type: 'custom' }]);
  };

  const removeSignatory = (id) => {
    setSignatories(signatories.filter(s => s.id !== id));
  };

  const updateSignatory = (id, value) => {
    setSignatories(signatories.map(s => s.id === id ? { ...s, designation: value } : s));
  };

  const handleExcelExport = async () => {
    if (downloadType === 'single' && !selectedStudent) {
      alert('Please select a student');
      return;
    }
    setExportLoading(true);
    setShowDownloadModal(false);
    try {
      const exportData = downloadType === 'all' ? filteredStudents : [selectedStudent];
      const response = await api.post('/api/coe/marks/export/excel', {
        students: exportData,
        subject: subject,
        context: context,
        signatories: signatories,
        orgInfo: orgInfo
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${subject.subjectCode}_marks_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
      alert('Failed to generate Excel report');
    } finally {
      setExportLoading(false);
    }
  };

  const handlePDFExport = async () => {
    if (downloadType === 'single' && !selectedStudent) {
      alert('Please select a student');
      return;
    }
    setExportLoading(true);
    setShowDownloadModal(false);
    try {
      const exportData = downloadType === 'all' ? filteredStudents : [selectedStudent];
      const response = await api.post('/api/coe/marks/export/pdf', {
        students: exportData,
        subject: subject,
        context: context,
        signatories: signatories,
        orgInfo: orgInfo
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${subject.subjectCode}_marks_${Date.now()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF export failed:', err);
      alert('Failed to generate PDF report');
    } finally {
      setExportLoading(false);
    }
  };

  const viewStudentDetails = (student) => {
    const subjectData = student.subjects.find(s => s.subjectId === subject.subjectId);
    setSelectedStudentDetails({ ...student, subjectData });
    setShowDetailsModal(true);
  };

  if (!context || !subject) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <div className="w-12 h-12 bg-rose-50 border border-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-rose-500" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900">Missing Context</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">No subject selected or configuration session expired. Please return to your active control ledger.</p>
          <button 
            onClick={goBackToDashboard}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium text-sm rounded-xl transition-colors shadow-sm"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 antialiased selection:bg-indigo-100 pb-12">
      {/* Dynamic Context Header Bar */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/80 px-4 py-3 sm:px-6">
        <div className="max-w-7xl mx-auto flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={goBackToDashboard}
              className="p-2 -ml-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
              aria-label="Back to dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate">{subject.subjectName}</h1>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 font-medium">
                <span className="font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">{subject.subjectCode}</span>
                <span>•</span>
                <span>{subject.isLab ? 'Lab' : 'Theory'}</span>
                <span>•</span>
                <span className="truncate text-indigo-600">{context.termName || context.semesterName || 'Term'}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button 
              onClick={() => {
                setDownloadType('all');
                setSelectedStudent(null);
                setShowDownloadModal(true);
              }}
              disabled={exportLoading}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-sm rounded-xl transition-colors shadow-sm disabled:opacity-50"
            >
              {exportLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span>Export Ledger</span>
            </button>
            <button 
              onClick={fetchSubjectMarks} 
              className="p-2 border border-slate-200 bg-white hover:bg-slate-50 rounded-xl transition-colors shadow-sm text-slate-600"
              aria-label="Refresh student roster"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-600' : ''}`} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 space-y-6">
        {/* Performance Metrics Cards */}
        {summary && (
          <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Enrolled</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{summary.totalStudents}</p>
              </div>
              <div className="hidden sm:flex p-3 bg-slate-50 rounded-xl text-slate-600"><Users className="w-5 h-5" /></div>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Eligible</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-600 mt-1">{summary.eligibleCount}</p>
              </div>
              <div className="hidden sm:flex p-3 bg-emerald-50 rounded-xl text-emerald-600"><UserCheck className="w-5 h-5" /></div>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Not Eligible</p>
                <p className="text-xl sm:text-2xl font-bold text-rose-600 mt-1">{summary.notEligibleCount}</p>
              </div>
              <div className="hidden sm:flex p-3 bg-rose-50 rounded-xl text-rose-600"><UserX className="w-5 h-5" /></div>
            </div>
            
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Class Avg</p>
                <p className="text-xl sm:text-2xl font-bold text-indigo-600 mt-1">{summary.averageCIEPercentage}%</p>
              </div>
              <div className="hidden sm:flex p-3 bg-indigo-50 rounded-xl text-indigo-600"><TrendingUp className="w-5 h-5" /></div>
            </div>
          </section>
        )}

        {/* Academic Faculty Meta Section */}
        {(facultyDetails || subject.facultyName) && (
          <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-xl shrink-0">
                <GraduationCap className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Instructor-in-Charge</h3>
                <p className="text-sm font-semibold text-slate-800 mt-0.5">
                  {facultyDetails?.facultyName || subject.facultyName || 'Unassigned Status'}
                </p>
              </div>
            </div>
            <div className="border-t sm:border-t-0 sm:border-l border-slate-100 pt-3 sm:pt-0 sm:pl-6">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Ledge Rule Execution</h4>
              <p className="text-sm font-medium text-slate-600 mt-0.5">
                {formatDate(facultyDetails?.lastCalculated)}
              </p>
            </div>
          </section>
        )}

        {/* Search Control Block */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search roster by candidate name or institutional USN..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-sm"
          />
        </div>

        {/* Dynamic Content States */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-2xl shadow-sm">
            <RefreshCw className="w-7 h-7 animate-spin text-indigo-600 mb-2" />
            <p className="text-sm text-slate-500 font-medium">Compiling dynamic performance aggregates...</p>
          </div>
        ) : error ? (
          <div className="bg-rose-50/50 border border-rose-200 rounded-2xl p-8 text-center max-w-lg mx-auto shadow-sm">
            <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-3" />
            <h4 className="text-slate-900 font-semibold text-base">Roster Compilation Exception</h4>
            <p className="text-sm text-rose-600 mt-1">{error}</p>
            <button onClick={fetchSubjectMarks} className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 font-medium text-sm rounded-xl transition-colors shadow-sm">
              <RefreshCw className="w-3.5 h-3.5" /> Re-execute Fetch
            </button>
          </div>
        ) : paginatedStudents.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-900">No Student Records Found</p>
            <p className="text-xs text-slate-400 mt-0.5">Refine query tokens or review active registration catalogs.</p>
          </div>
        ) : (
          <>
            {/* Mobile View Roster (Card List Layout) */}
            <div className="grid grid-cols-1 gap-3 md:hidden">
              {paginatedStudents.map((student) => {
                const subjectData = student.subjects.find(s => s.subjectId === subject.subjectId);
                const profileImageUrl = getProfileImageUrl(student.profilePhotoUrl);
                const initials = student.studentName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'ST';
                
                return (
                  <div key={student.studentId} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200/60 flex items-center justify-center overflow-hidden shrink-0">
                        {profileImageUrl ? (
                          <img 
                            src={profileImageUrl} 
                            alt={student.studentName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-slate-600">${initials}</span>`;
                            }}
                          />
                        ) : (
                          <span className="text-xs font-bold text-slate-600">${initials}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-slate-900 truncate">{student.studentName}</p>
                        <p className="text-xs font-mono font-medium text-slate-500 mt-0.5">{student.studentUSN || 'N/A'}</p>
                      </div>
                      <div>{getEligibilityBadge(subjectData?.isEligible || false)}</div>
                    </div>

                    <div className="grid grid-cols-2 border-y border-slate-100 py-2.5 my-0.5 text-center bg-slate-50/50 rounded-xl px-2">
                      <div>
                        <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wide">CIE Contribution</span>
                        <span className="block text-sm font-bold text-slate-800 mt-0.5">
                          {subjectData?.cieScore || 0} <span className="text-xs text-slate-400 font-normal">/ {subjectData?.cieMaxMarks || 40}</span>
                        </span>
                        <span className="inline-block text-[11px] font-medium text-indigo-600 mt-0.5 bg-indigo-50 border border-indigo-100 px-1.5 rounded">{subjectData?.ciePercentage || 0}%</span>
                      </div>
                      <div className="border-l border-slate-200/60">
                        <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wide">Attendance Metric</span>
                        <span className="block text-sm font-bold text-slate-800 mt-0.5">
                          {subjectData?.attendancePercentage || 0}%
                        </span>
                        <span className="inline-block text-[11px] font-medium text-slate-500 mt-0.5 bg-slate-100 border border-slate-200/40 px-1.5 rounded">Req: {subjectData?.attendanceRequired || 75}%</span>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => viewStudentDetails(student)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-50 border border-slate-200 hover:bg-slate-100 rounded-xl text-xs font-semibold text-slate-700 transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" /> Component Scores
                      </button>
                      <button
                        onClick={() => {
                          setSelectedStudent(student);
                          setDownloadType('single');
                          setShowDownloadModal(true);
                        }}
                        className="inline-flex items-center justify-center p-2 bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 rounded-xl transition-colors"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop View Roster (Dense Grid Layout) */}
            <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-12 text-center">Photo</th>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider w-36">University USN</th>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Student Name & Class</th>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center w-40">CIE Aggregate</th>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center w-40">Attendance Log</th>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center w-36">Status</th>
                      <th className="p-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-center w-28">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedStudents.map((student) => {
                      const subjectData = student.subjects.find(s => s.subjectId === subject.subjectId);
                      const profileImageUrl = getProfileImageUrl(student.profilePhotoUrl);
                      const initials = student.studentName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'ST';
                      
                      return (
                        <tr key={student.studentId} className="hover:bg-slate-50/50 transition-colors">
                          <td className="p-4 text-center">
                            <div className="w-9 h-9 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden mx-auto">
                              {profileImageUrl ? (
                                <img 
                                  src={profileImageUrl} 
                                  alt={student.studentName}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<span class="text-xs font-bold text-slate-600">${initials}</span>`;
                                  }}
                                />
                              ) : (
                                <span className="text-xs font-bold text-slate-600">{initials}</span>
                              )}
                            </div>
                          </td>
                          <td className="p-4 font-mono font-medium text-slate-600">{student.studentUSN || 'N/A'}</td>
                          <td className="p-4">
                            <div>
                              <p className="font-semibold text-slate-800">{student.studentName}</p>
                              <p className="text-xs text-slate-400 mt-0.5">{student.className}</p>
                            </div>
                          </td>
                          <td className="p-4 text-center">
                            <span className="font-bold text-slate-800">
                              {subjectData?.cieScore || 0} <span className="text-xs font-normal text-slate-400">/ {subjectData?.cieMaxMarks || 40}</span>
                            </span>
                            <span className="block text-[11px] font-semibold text-indigo-600 mt-0.5">{subjectData?.ciePercentage || 0}%</span>
                          </td>
                          <td className="p-4 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${getAttendanceColor(subjectData?.attendancePercentage || 0)}`}>
                              {subjectData?.attendancePercentage || 0}%
                            </span>
                            <span className="block text-[11px] text-slate-400 mt-1">Req: {subjectData?.attendanceRequired || 75}%</span>
                          </td>
                          <td className="p-4 text-center">
                            {getEligibilityBadge(subjectData?.isEligible || false)}
                          </td>
                          <td className="p-4">
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => viewStudentDetails(student)}
                                className="p-2 text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 border border-transparent hover:border-indigo-100 rounded-xl transition-all"
                                title="Review individual metrics Breakdown"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setDownloadType('single');
                                  setShowDownloadModal(true);
                                }}
                                className="p-2 text-slate-600 hover:text-emerald-600 hover:bg-emerald-50 border border-transparent hover:border-emerald-100 rounded-xl transition-all"
                                title="Export single performance card"
                              >
                                <Download className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Segment */}
            {totalPages > 1 && (
              <footer className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                <p className="text-xs font-medium text-slate-500 order-2 sm:order-1 text-center sm:text-left">
                  Displaying record lines <span className="font-bold text-slate-800">{(currentPage - 1) * 10 + 1}</span> to <span className="font-bold text-slate-800">{Math.min(currentPage * 10, filteredStudents.length)}</span> out of <span className="font-bold text-slate-800">{filteredStudents.length}</span> candidates
                </p>
                <div className="flex gap-1.5 justify-center order-1 sm:order-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-slate-200 bg-white hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-40 text-slate-600"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-slate-200 bg-white hover:bg-slate-50 rounded-xl transition-colors disabled:opacity-40 text-slate-600"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </footer>
            )}
          </>
        )}
      </main>

      {/* Student Details Component-wise Modal */}
      {showDetailsModal && selectedStudentDetails && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-in fade-in duration-200" onClick={() => setShowDetailsModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] sm:max-h-[85vh] overflow-y-auto shadow-xl flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between z-10">
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 truncate">{selectedStudentDetails.studentName}</h3>
                <p className="text-xs text-slate-500 mt-0.5 font-medium font-mono">{selectedStudentDetails.studentUSN} | {selectedStudentDetails.className}</p>
              </div>
              <button onClick={() => setShowDetailsModal(false)} className="p-2 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-xl transition-colors shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-5 space-y-5 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 text-center">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">CIE Score Summary</p>
                  <p className="text-lg font-black text-indigo-600 mt-1">
                    {formatTo2(selectedStudentDetails.subjectData?.cieScore)} <span className="text-xs font-semibold text-slate-400">/ {formatTo2(selectedStudentDetails.subjectData?.cieMaxMarks || 40)}</span>
                  </p>
                  <span className="inline-block text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100/60 px-1.5 py-0.5 rounded mt-1.5">{formatTo2(selectedStudentDetails.subjectData?.ciePercentage)}% Ratio</span>
                </div>
                <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 text-center">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Attendance Index</p>
                  <p className={`text-lg font-black mt-1 ${(selectedStudentDetails.subjectData?.attendancePercentage || 0) >= 75 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {formatTo2(selectedStudentDetails.subjectData?.attendancePercentage)}%
                  </p>
                  <span className="inline-block text-[10px] font-bold text-slate-500 bg-slate-200/50 px-1.5 py-0.5 rounded mt-1.5">Required: {formatTo2(selectedStudentDetails.subjectData?.attendanceRequired || 75)}%</span>
                </div>
              </div>

              <div className={`p-4 rounded-xl border ${selectedStudentDetails.subjectData?.isEligible ? 'bg-emerald-50/50 border-emerald-100 text-emerald-800' : 'bg-rose-50/50 border-rose-100 text-rose-800'}`}>
                <div className="flex items-center gap-2 mb-1">
                  {selectedStudentDetails.subjectData?.isEligible ? 
                    <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" /> : 
                    <XCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  }
                  <span className="font-bold text-sm tracking-wide">
                    {selectedStudentDetails.subjectData?.isEligible ? 'Clearance Issued' : 'Not Eligible'}
                  </span>
                </div>
                <p className="text-xs text-slate-600 font-medium leading-relaxed pl-6 mt-0.5">{selectedStudentDetails.subjectData?.eligibilityReason}</p>
              </div>

              <div>
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-indigo-500" />
                  Component Evaluation Matrix
                </h4>
                <div className="space-y-3">
                  {selectedStudentDetails.subjectData?.componentScores?.map((component, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-4 bg-white shadow-sm">
                      <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-50">
                        <div className="flex items-center gap-2">
                          {component.componentName === 'Attendance' ? <Clock className="w-4 h-4 text-amber-500" /> : <BookOpen className="w-4 h-4 text-indigo-500" />}
                          <span className="font-bold text-slate-800 text-sm">{component.componentName}</span>
                        </div>
                        <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                          Contribution: {formatTo2(component.contributionToCIE)} / {formatTo2(component.maxPossibleContribution)}
                        </span>
                      </div>
                      <div className="text-xs font-medium text-slate-400">
                        Raw Continuous Score Evaluation: <span className="text-slate-700 font-bold">{formatTo2(component.rawScore)}</span> / {formatTo2(component.maxRawScore)} ({formatTo2(component.componentPercentage)}%)
                      </div>
                      {Array.isArray(component.itemsIncluded) && component.itemsIncluded.length > 0 && (
                        <div className="mt-3.5 pt-2.5 border-t border-dashed border-slate-100">
                          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Itemized Breakdown:</div>
                          <div className="space-y-2">
                            {component.itemsIncluded.map((item, iidx) => (
                              <div key={item.itemId || iidx} className="p-2 bg-slate-50 border border-slate-100 rounded-lg text-xs flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
                                <span className="font-semibold text-slate-700">{item.itemName || `Item ID: ${item.itemId}`}</span>
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 font-medium">
                                  <span>Raw: <strong className="text-slate-700">{formatTo2(item.rawScore)}/{formatTo2(item.maxRawScore)}</strong></span>
                                  <span>Ratio: <strong>{formatTo2(item.percentage)}%</strong></span>
                                  <span className="text-indigo-600 bg-indigo-50/50 px-1 rounded">Net: <strong>{formatTo2(item.contributionToCIE)}</strong></span>
                                  {item.aggregationRuleApplied && (
                                    <span className="text-amber-700 bg-amber-50 px-1 rounded font-mono text-[10px]">Rule: {item.aggregationRuleApplied}</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Download / Export Modal Setup */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-in fade-in duration-200" onClick={() => setShowDownloadModal(false)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full shadow-xl flex flex-col animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 text-sm sm:text-base">Export Marks Documentation</h3>
              <button onClick={() => setShowDownloadModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2.5">Scope Configuration</p>
                <div className="flex gap-4 p-2.5 bg-slate-50 border border-slate-100 rounded-xl">
                  <label className="flex-1 flex items-center justify-center gap-2 py-1.5 px-3 bg-white border border-slate-200 rounded-lg shadow-sm text-xs font-semibold text-slate-700 cursor-pointer has-[:checked]:border-indigo-600 has-[:checked]:text-indigo-600 has-[:checked]:ring-1 has-[:checked]:ring-indigo-600/30 transition-all">
                    <input type="radio" checked={downloadType === 'all'} onChange={() => setDownloadType('all')} className="sr-only" /> Bulk Ledger
                  </label>
                  <label className="flex-1 flex items-center justify-center gap-2 py-1.5 px-3 bg-white border border-slate-200 rounded-lg shadow-sm text-xs font-semibold text-slate-700 cursor-pointer has-[:checked]:border-indigo-600 has-[:checked]:text-indigo-600 has-[:checked]:ring-1 has-[:checked]:ring-indigo-600/30 transition-all">
                    <input type="radio" checked={downloadType === 'single'} onChange={() => setDownloadType('single')} className="sr-only" /> Filtered Single
                  </label>
                </div>
              </div>

              {downloadType === 'single' && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Target Candidate</label>
                  <select 
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all shadow-sm"
                    onChange={(e) => setSelectedStudent(students.find(s => s.studentId === e.target.value))}
                  >
                    <option value="">Choose candidate signature profile</option>
                    {students.map(s => <option key={s.studentId} value={s.studentId}>{s.studentName} ({s.studentUSN || 'N/A'})</option>)}
                  </select>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Signatory Protocol Ledger</p>
                  <button onClick={addSignatory} className="inline-flex items-center gap-1 text-[11px] text-indigo-600 font-bold hover:underline"><Plus className="w-3 h-3" /> Add Tier</button>
                </div>
                <div className="space-y-2 max-h-36 overflow-y-auto pr-1">
                  {signatories.map(sig => (
                    <div key={sig.id} className="flex gap-2 items-center animate-in fade-in slide-in-from-top-1 duration-150">
                      <input 
                        type="text" 
                        className="flex-1 p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:border-indigo-500 focus:bg-white transition-all" 
                        value={sig.designation} 
                        placeholder="e.g. Dean of Academic Affairs"
                        onChange={(e) => updateSignatory(sig.id, e.target.value)} 
                      />
                      <button onClick={() => removeSignatory(sig.id)} className="p-2 border border-transparent hover:border-rose-100 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={handleExcelExport} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs rounded-xl shadow-sm transition-colors">
                  <FileSpreadsheet className="w-4 h-4" /> Microsoft Excel
                </button>
                <button onClick={handlePDFExport} className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-semibold text-xs rounded-xl shadow-sm transition-colors">
                  <FileText className="w-4 h-4" /> Academic PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default COESubjectMarks;